import React, { useRef } from 'react';
import ReactDOM from 'react-dom';
import { useEditor, useNode } from '@craftjs/core';
import { useSelector } from 'react-redux';
import styles from './styles/styles.module.css';
import { useInteractionMode } from '../contexts/InteractionMode';

/** Body portal to ensure the modal sits above all stacking contexts */
function BodyPortal({ children }: { children: React.ReactNode }) {
  const el = React.useMemo(() => document.createElement('div'), []);
  React.useEffect(() => {
    document.body.appendChild(el);
    return () => {
      document.body.removeChild(el);
    };
  }, [el]);
  return ReactDOM.createPortal(children, el);
}

const CheckboxField = ({
  label,
  placeholder,
  value,
  left = 0,
  top = 0,
  width = 160,
  height = 40,
}: any) => {
  const { enabled, actions } = useEditor((state) => ({
    enabled: state.options.enabled,
  }));

  const {
    connectors: { connect },
    setProp,
    id,
  } = useNode();

  const { selected } = useSelector((state: any) => state.formToggle);
  const interactionMode = useInteractionMode();

  const [isDragging, setDragging] = React.useState(false);

  const [showEditor, setShowEditor] = React.useState(false);

  const wrapperRef = useRef<HTMLDivElement | null>(null);

  // Close modal on Escape
  React.useEffect(() => {
    if (!showEditor) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setShowEditor(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [showEditor]);

  // Drag in BUILD/REPORT
  const onMouseDown = (e: React.MouseEvent) => {
    if (!enabled || showEditor) return;
    e.preventDefault();
    setDragging(true);

    const startX = e.clientX;
    const startY = e.clientY;
    const initialLeft = left;
    const initialTop = top;

    const onMouseMove = (ev: MouseEvent) => {
      const dx = ev.clientX - startX;
      const dy = ev.clientY - startY;
      setProp((props: any) => {
        props.left = initialLeft + dx;
        props.top = initialTop + dy;
      });
    };

    const onMouseUp = () => {
      setDragging(false);
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  };

  // Open modal editor — only in FORM
  const onDoubleClick = () => {
    if (!enabled || selected !== 'FORM') return;
    setShowEditor(true);
  };

  // Delete — only in FORM
  const handleDelete = () => {
    if (selected !== 'FORM') return;
    actions.delete(id);
  };

  function cls() {
    switch (interactionMode) {
      case 'BUILD':
        return {
          wrapper: styles.wrapperForm ?? styles.wrapper,
          inputContainer: styles.inputContainerForm ?? styles.inputContainer,
          label: styles.labelForm ?? styles.label,
          placeholder: styles.placeholderReport ?? styles.label,
        };
      case 'VIEW':
        return {
          wrapper: styles.wrapperReport ?? styles.wrapper,
          inputContainer: styles.inputContainerReport ?? styles.inputContainer,
          label: styles.labelReport ?? styles.label,
          placeholder: styles.placeholderReport ?? styles.label,
        };
      case 'FILL':
        return {
          wrapper: styles.wrapperForm ?? styles.wrapper,
          inputContainer: styles.inputContainerForm ?? styles.inputContainer,
          label: styles.labelForm ?? styles.label,
          placeholder: styles.placeholderReport ?? styles.label,
        };
      default:
        return { wrapper: '' } as any;
    }
  }
  console.log(value);
  return (
    <>
      <div
        ref={(ref) => {
          wrapperRef.current = ref;
          if (ref) connect(ref);
        }}
        className={cls().wrapper}
        style={{
          left,
          top,
          position: 'absolute',
          cursor: enabled ? (isDragging ? 'grabbing' : 'grab') : 'default',
        }}
      >
        {/* VIEW: show label/value */}
        {interactionMode === 'VIEW' && value && (
          <div className={styles.viewContainer}>
            <p className={styles.viewLabel}>{label}</p>
          </div>
        )}

        {/* BUILD/FILL: interactive */}
        {interactionMode !== 'VIEW' && (
          <div
            className={cls().inputContainer}
            style={{ width, height }}
            onMouseDown={onMouseDown}
            onDoubleClick={onDoubleClick}
          >
            <label className={cls().label}>
              <input
                type="checkbox"
                checked={!!value}
                disabled={
                  interactionMode !== 'FILL'
                } /* don't toggle while designing */
                onChange={(e) =>
                  interactionMode === 'FILL' &&
                  setProp((props: any) => {
                    props.value = e.target.checked;
                  })
                }
                style={{ pointerEvents: enabled ? 'none' : 'auto' }}
              />
              {label}
              {enabled && (
                <div className={styles.deleteButton} onClick={handleDelete}>
                  ×
                </div>
              )}
            </label>

            {/* Uncomment if you want resize in FORM */}
            {/* {enabled && selected === 'FORM' && (
              <div onMouseDown={onResizeMouseDown} className={styles.resizeHandle} />
            )} */}
          </div>
        )}
      </div>

      {/* Centered modal editor (FORM only) */}
      {showEditor && selected === 'FORM' && (
        <BodyPortal>
          <div
            className={styles.modalOverlay}
            role="presentation"
            onMouseDown={() =>
              setShowEditor(false)
            } /* click backdrop to close */
          >
            <div
              className={styles.modalPanel}
              role="dialog"
              aria-modal="true"
              aria-label="Edit checkbox"
              onMouseDown={(e) => e.stopPropagation()} /* keep clicks inside */
            >
              <div className={styles.modalHeader}>
                <h3 className={styles.modalTitle}>Edit Checkbox</h3>
                <button
                  className={styles.modalClose}
                  aria-label="Close"
                  onClick={() => setShowEditor(false)}
                >
                  ×
                </button>
              </div>

              <div className={styles.modalBody}>
                <div className={styles.formRow}>
                  <label className={styles.rowLabel}>Label</label>
                  <input
                    className={styles.rowInput}
                    type="text"
                    value={label}
                    onChange={(e) =>
                      setProp((props: any) => (props.label = e.target.value))
                    }
                  />
                </div>
              </div>

              <div className={styles.modalFooter}>
                <button
                  className={styles.btnPrimary}
                  onClick={() => setShowEditor(false)}
                >
                  Done
                </button>
              </div>
            </div>
          </div>
        </BodyPortal>
      )}
    </>
  );
};

(CheckboxField as any).craft = {
  displayName: 'CheckboxField',
  props: {
    label: 'Check me',
    placeholder: '—',
    value: false,
    left: 0,
    top: 0,
    width: 160,
    height: 40,
  },
};

export default CheckboxField;
